/**
 * Release 3 REV-1 — one line per notification kind (same wording as the worker's mail templates) and the deep link
 * the inbox opens: the asset for asset / comment subjects, the board otherwise.
 */
import type { Notification, NotificationKind } from '@/lib/api/queries';

const str = (v: unknown): string | null => (typeof v === 'string' && v.length ? v : null);
const num = (v: unknown): number | null => (typeof v === 'number' ? v : null);

export const KIND_LABELS: Record<NotificationKind, string> = {
  mention: 'Mentions',
  reply: 'Replies',
  comment: 'Comments',
  approval: 'Approvals',
  upload: 'Uploads',
  share_opened: 'Share opened',
  share_downloaded: 'Share downloads',
  guest_upload: 'Guest uploads',
  board_invite: 'Board invites',
  rights_expired: 'Rights expired',
  retention_applied: 'Retention',
};

/** Filter groups shown in the inbox header. */
export const KIND_FILTERS: Array<{ id: string; label: string; kinds: NotificationKind[] }> = [
  { id: 'all', label: 'All', kinds: [] },
  { id: 'mentions', label: 'Mentions & replies', kinds: ['mention', 'reply'] },
  { id: 'comments', label: 'Comments', kinds: ['comment'] },
  { id: 'approvals', label: 'Approvals', kinds: ['approval'] },
  { id: 'uploads', label: 'Uploads', kinds: ['upload', 'guest_upload'] },
  { id: 'shares', label: 'Shares', kinds: ['share_opened', 'share_downloaded'] },
  { id: 'governance', label: 'Rights & retention', kinds: ['rights_expired', 'retention_applied', 'board_invite'] },
];

export function notificationLine(n: Pick<Notification, 'kind' | 'payload' | 'actor'>): string {
  const p = n.payload ?? {};
  const who = n.actor?.display_name ?? str(p.guest_name) ?? 'Someone';
  const title = str(p.asset_title) ?? str(p.filename) ?? 'an asset';
  const board = str(p.board_name) ?? 'a board';
  const count = num(p.count) ?? 0;
  switch (n.kind) {
    case 'mention': return `${who} mentioned you on ${title}`;
    case 'reply': return `${who} replied to your comment on ${title}`;
    case 'comment': return `${who} commented on ${title}`;
    case 'approval': return `${who} set ${title} to ${str(p.to) ?? 'a new approval status'}`;
    case 'upload': return `${who} uploaded ${title}`;
    case 'guest_upload': return `${who} (guest) uploaded ${title}`;
    case 'share_opened': return 'Your share link was opened';
    case 'share_downloaded': return 'Files were downloaded from your share link';
    case 'board_invite': return `${who} invited you to ${board}`;
    case 'rights_expired': return `Usage rights expired on ${count} asset${count === 1 ? '' : 's'}`;
    case 'retention_applied': return `Retention archived ${count} asset${count === 1 ? '' : 's'}`;
    default: return `${who}: ${n.kind}`;
  }
}

export function notificationExcerpt(n: Pick<Notification, 'payload'>): string | null {
  return str(n.payload?.excerpt) ?? str(n.payload?.message);
}

/** Route inside the workspace (`/w/:ws` prefix added by the caller). */
export function notificationTarget(n: Pick<Notification, 'subject_type' | 'subject_id' | 'board_id' | 'payload'>): string {
  const assetId = str(n.payload?.asset_id) ?? (n.subject_type === 'asset' ? n.subject_id : null);
  if (assetId) return `/a/${assetId}`;
  if (n.subject_type === 'board') return `/b/${n.subject_id}`;
  if (n.board_id) return `/b/${n.board_id}`;
  return '';
}
